import type { IntlShape } from 'react-intl';

import { isFreeTrialLevel } from '../termedLevel/freeTrialLevel';
import { isIntegratedOfferLevel } from '../termedLevel/integratedOfferLevel';
import { type Period, PeriodUnit, type TermedLevel } from '../termedLevel/types';
import { formatDurationUnit } from './formatDurationUnit';

export const getProductLabelFromContract = (contract: TermedLevel, intl: IntlShape) => {
    if (isIntegratedOfferLevel(contract.level)) {
        return `${contract.variantName} (${intl.formatMessage(
            { id: 'intl-msg:marketplace.integrated.offer.contract.label' },
            {
                contractPeriod: contract.periodLength,
                contractUnit: formatDurationUnit(contract.periodUnit, intl, contract.periodLength),
            }
        )})`;
    }

    return getLocalizedLabel(contract.variantName, { unit: contract.periodUnit, length: contract.periodLength }, intl);
};

export const getRenewsToProductLabelFromContract = (contract: TermedLevel, intl: IntlShape): string => {
    const {
        renewsToLevelName,
        renewsToLevelVariantName,
        renewsToLevelContractPeriodLength,
        renewsToLevelContractPeriodUnit,
    } = contract;
    const contractPeriod: Period | undefined =
        renewsToLevelContractPeriodLength && renewsToLevelContractPeriodUnit
            ? {
                  unit: renewsToLevelContractPeriodUnit,
                  length: renewsToLevelContractPeriodLength,
              }
            : undefined;
    return getLocalizedLabel(renewsToLevelVariantName ?? renewsToLevelName ?? '', contractPeriod, intl);
};

export const getProductLabel = (
    product: { variantName?: string; productName?: string; name?: string; contractPeriod?: Period; level?: string },
    intl: IntlShape
): string => {
    if (isFreeTrialLevel(product.level)) {
        return product.productName ?? product.name ?? '';
    }
    if (isIntegratedOfferLevel(product.level) && product.contractPeriod?.unit) {
        return `${product.variantName} (${intl.formatMessage(
            { id: 'intl-msg:marketplace.integrated.offer.contract.label' },
            {
                contractPeriod: product.contractPeriod.length,
                contractUnit: formatDurationUnit(product.contractPeriod.unit, intl, product.contractPeriod.length),
            }
        )})`;
    }
    return getLocalizedLabel(
        product.variantName ?? product.productName ?? product.name ?? '',
        product.contractPeriod,
        intl
    );
};

export const getLocalizedLabel = (name: string, contractPeriod: Period | undefined, intl: IntlShape): string => {
    if (contractPeriod) {
        const localizedPeriod = getLocalizedPeriod(contractPeriod, intl);
        return `${name} (${localizedPeriod})`;
    }
    return name;
};

export const getLocalizedPeriod = (contractPeriod: Period, intl: IntlShape) => {
    if (contractPeriod && contractPeriod.length > 0) {
        const durationUnitIntlId = getDurationUnitIntlId(contractPeriod);
        if (!durationUnitIntlId) {
            console.error(
                `${contractPeriod.unit} is not a valid ContractPeriodUnit. Contract duration will not be rendered.`
            );
            return '';
        }
        return `${contractPeriod.length} ${intl.formatMessage({ id: durationUnitIntlId })}`;
    }
    return '';
};

const getDurationUnitIntlId = (contractPeriod: Period): string | undefined => {
    switch (contractPeriod.unit) {
        case PeriodUnit.YEAR:
            return contractPeriod.length === 1
                ? 'intl-msg:marketplace.durationUnit.year'
                : 'intl-msg:marketplace.durationUnit.years';
        case PeriodUnit.MONTH:
            return contractPeriod.length === 1
                ? 'intl-msg:marketplace.durationUnit.month'
                : 'intl-msg:marketplace.durationUnit.months';
        case PeriodUnit.DAY:
            return contractPeriod.length === 1
                ? 'intl-msg:marketplace.durationUnit.day'
                : 'intl-msg:marketplace.durationUnit.days';
        default:
            return;
    }
};
